import React, { useState, useEffect } from "react";

const RotatingSwitchGame = () => {
  // 生成随机映射
  const generateRandomMapping = (count) => {
    const randomMapping = Array.from({ length: count }, (_, i) => i);
    const initialRotation = Math.floor(Math.random() * count);
    for (let i = 0; i < initialRotation; i++) {
      randomMapping.unshift(randomMapping.pop());
    }
    return randomMapping;
  };

  // 生成随机灯光状态（点亮1-3个灯）
  const generateRandomLights = (count) => {
    const lights = Array(count).fill(false);
    const numLightsToTurnOn = Math.floor(Math.random() * 3) + 1; // 1-3个灯
    const indices = Array.from({ length: count }, (_, i) => i);

    // 随机选择要点亮的灯
    for (let i = 0; i < numLightsToTurnOn; i++) {
      const randomIndex = Math.floor(Math.random() * indices.length);
      const lightIndex = indices.splice(randomIndex, 1)[0];
      lights[lightIndex] = true;
    }

    return lights;
  };

  // 初始化状态
  const initializeGame = (count) => {
    const initialMapping = generateRandomMapping(count);
    const initialLights = generateRandomLights(count);
    return { initialMapping, initialLights };
  };

  // 初始化
  const { initialMapping: defaultMapping, initialLights: defaultLights } =
    initializeGame(4);

  // 灯的状态 (true = 亮, false = 灭)
  const [lights, setLights] = useState(defaultLights);

  // 灯的数量
  const [lightCount, setLightCount] = useState(4);

  // 开关到灯的映射关系 (switches[i] 控制 lights[mapping[i]])
  const [mapping, setMapping] = useState(defaultMapping);

  // 初始映射（用于重置）
  const [initialMapping, setInitialMapping] = useState(defaultMapping);

  // 初始灯光状态（用于重置）
  const [initialLights, setInitialLights] = useState(defaultLights);

  // 选中的开关
  const [selectedSwitches, setSelectedSwitches] = useState([
    false,
    false,
    false,
    false,
  ]);

  // 游戏设置
  const [showMapping, setShowMapping] = useState(false); // 是否显示映射关系
  const [moves, setMoves] = useState(0);
  const [history, setHistory] = useState([]);
  const [gameMode, setGameMode] = useState("challenge"); // 'challenge' 或 'exploration'
  const [lastRotationStep, setLastRotationStep] = useState(null); // 记录上次旋转步数

  // 检查是否所有灯都亮着
  const isComplete = lights.every((light) => light);

  // 改变灯的数量
  const changeLightCount = (newCount) => {
    setLightCount(newCount);

    // 重新初始化状态
    const {
      initialMapping: newInitialMapping,
      initialLights: newInitialLights,
    } = initializeGame(newCount);
    const newSelectedSwitches = Array(newCount).fill(false);

    setLights(newInitialLights);
    setMapping(newInitialMapping);
    setInitialMapping(newInitialMapping);
    setInitialLights(newInitialLights);
    setSelectedSwitches(newSelectedSwitches);
    setMoves(0);
    setHistory([]);
    setLastRotationStep(null);
  };

  // 生成随机初始状态
  const generateRandomState = () => {
    // 随机初始映射
    const randomMapping = Array.from({ length: lightCount }, (_, i) => i);
    const initialRotation = Math.floor(Math.random() * lightCount);
    for (let i = 0; i < initialRotation; i++) {
      randomMapping.unshift(randomMapping.pop());
    }

    // 随机灯的初始状态
    const randomLights = Array(lightCount)
      .fill(false)
      .map(() => Math.random() < 0.3);

    setMapping([...randomMapping]);
    setInitialMapping([...randomMapping]);
    setLights([...randomLights]);
    setMoves(0);
    setHistory([]);
    setSelectedSwitches(Array(lightCount).fill(false));
    setLastRotationStep(null);
  };

  // 重置游戏
  const resetGame = () => {
    setLights(Array(lightCount).fill(false));
    setMapping([...initialMapping]);
    setMoves(0);
    setHistory([]);
    setSelectedSwitches(Array(lightCount).fill(false));
    setLastRotationStep(null);
  };

  // 切换开关选择
  const toggleSwitch = (index) => {
    const newSelected = [...selectedSwitches];
    newSelected[index] = !newSelected[index];
    setSelectedSwitches(newSelected);
  };

  // 执行操作
  const executeOperation = () => {
    if (!selectedSwitches.some((s) => s)) return; // 没有选中任何开关

    // 1. 根据当前映射关系切换对应的灯
    const newLights = [...lights];
    selectedSwitches.forEach((isSelected, switchIndex) => {
      if (isSelected) {
        const lightIndex = mapping[switchIndex];
        newLights[lightIndex] = !newLights[lightIndex];
      }
    });

    // 2. 随机旋转映射关系（1到lightCount格）
    const randomRotationStep = Math.floor(Math.random() * lightCount) + 1;
    const newMapping = [...mapping];
    for (let i = 0; i < randomRotationStep; i++) {
      newMapping.unshift(newMapping.pop());
    }

    // 更新状态
    setLights(newLights);
    setMapping(newMapping);
    setMoves(moves + 1);
    setLastRotationStep(randomRotationStep);

    // 记录历史
    const operation = {
      switches: [...selectedSwitches],
      oldMapping: [...mapping],
      newMapping: [...newMapping],
      resultLights: [...newLights],
      rotationStep: randomRotationStep,
    };
    setHistory([...history, operation]);

    // 清除选择
    setSelectedSwitches(Array(lightCount).fill(false));
  };

  // 计算圆形布局的位置
  const getCirclePosition = (index, total, radius = 120) => {
    const angle = (2 * Math.PI * index) / total - Math.PI / 2; // 从顶部开始
    const x = radius * Math.cos(angle);
    const y = radius * Math.sin(angle);
    return { x: x + 200, y: y + 200 }; // 调整中心点偏移以适应更大的圆
  };

  // 开关组件（圆形布局版本）
  const CircularSwitch = ({ index, isSelected }) => {
    const position = getCirclePosition(index, lightCount, 80); // 内圈放开关

    return (
      <div
        className="absolute transform -translate-x-1/2 -translate-y-1/2"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        <div className="flex flex-col items-center space-y-1">
          <div className="text-xs font-medium text-gray-600">S{index + 1}</div>
          <button
            onClick={() => toggleSwitch(index)}
            className={`w-12 h-6 rounded-full transition-all duration-300 relative
              ${isSelected ? "bg-blue-500" : "bg-gray-300"}
              hover:scale-110 transform border-2 border-gray-400
            `}
          >
            <div
              className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all duration-300
              ${isSelected ? "left-6" : "left-0.5"}
            `}
            ></div>
          </button>
        </div>
      </div>
    );
  };

  // 灯泡组件（圆形布局版本）
  const CircularLight = ({ index, isOn }) => {
    const position = getCirclePosition(index, lightCount, 140); // 外圈放灯泡

    return (
      <div
        className="absolute transform -translate-x-1/2 -translate-y-1/2"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        <div className="flex flex-col items-center space-y-1">
          <div className="text-xs font-medium text-gray-600">L{index + 1}</div>
          <div
            className={`w-14 h-14 rounded-full border-4 transition-all duration-500
            ${
              isOn
                ? "bg-yellow-400 border-yellow-500 shadow-lg shadow-yellow-300"
                : "bg-gray-200 border-gray-400"
            }
          `}
          >
            <div className="w-full h-full rounded-full bg-white opacity-30"></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">旋转开关谜题</h2>
        <p className="text-gray-600 text-sm mb-4">
          {lightCount}个开关控制{lightCount}
          盏灯，但每次操作后对应关系会旋转！你能点亮所有灯吗？
        </p>
      </div>

      {/* 控制面板 */}
      <div className="flex flex-wrap justify-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">模式：</label>
          <button
            onClick={() => {
              setGameMode("challenge");
              setShowMapping(false);
            }}
            className={`px-3 py-1 rounded text-sm ${
              gameMode === "challenge"
                ? "bg-purple-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            挑战模式
          </button>
          <button
            onClick={() => {
              setGameMode("exploration");
              setShowMapping(true);
            }}
            className={`px-3 py-1 rounded text-sm ${
              gameMode === "exploration"
                ? "bg-purple-500 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            探究模式
          </button>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">灯数量：</label>
          <select
            value={lightCount}
            onChange={(e) => changeLightCount(Number(e.target.value))}
            className="px-2 py-1 border rounded text-sm"
          >
            {[3, 4, 5, 6, 7, 8].map((num) => (
              <option key={num} value={num}>
                {num}个灯
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={generateRandomState}
          className="px-4 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
        >
          新的挑战
        </button>

        <button
          onClick={resetGame}
          className="px-4 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
        >
          重置当前
        </button>

        {gameMode === "exploration" && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showMapping}
              onChange={(e) => setShowMapping(e.target.checked)}
            />
            显示映射关系
          </label>
        )}
      </div>

      {/* 游戏主区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* 圆形控制区域 */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium mb-4 text-center">操作台</h3>

          <div
            className="relative h-96 mx-auto mb-6"
            style={{ width: "400px" }}
          >
            {/* 绘制连线 */}
            <svg className="absolute inset-0 w-full h-full">
              {/* 外圈连线（灯泡之间） */}
              {lights.map((_, index) => {
                const current = getCirclePosition(index, lightCount, 140);
                const next = getCirclePosition(
                  (index + 1) % lightCount,
                  lightCount,
                  140
                );
                return (
                  <line
                    key={`light-${index}`}
                    x1={current.x}
                    y1={current.y}
                    x2={next.x}
                    y2={next.y}
                    stroke="#fbbf24"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    opacity="0.6"
                  />
                );
              })}

              {/* 内圈连线（开关之间） */}
              {selectedSwitches.map((_, index) => {
                const current = getCirclePosition(index, lightCount, 80);
                const next = getCirclePosition(
                  (index + 1) % lightCount,
                  lightCount,
                  80
                );
                return (
                  <line
                    key={`switch-${index}`}
                    x1={current.x}
                    y1={current.y}
                    x2={next.x}
                    y2={next.y}
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeDasharray="3,3"
                    opacity="0.4"
                  />
                );
              })}

              {/* 映射关系连线（探究模式下显示） */}
              {showMapping &&
                mapping.map((lightIndex, switchIndex) => {
                  const switchPos = getCirclePosition(
                    switchIndex,
                    lightCount,
                    80
                  );
                  const lightPos = getCirclePosition(
                    lightIndex,
                    lightCount,
                    140
                  );
                  return (
                    <line
                      key={`mapping-${switchIndex}`}
                      x1={switchPos.x}
                      y1={switchPos.y}
                      x2={lightPos.x}
                      y2={lightPos.y}
                      stroke="#ef4444"
                      strokeWidth="2"
                      opacity="0.7"
                    />
                  );
                })}
            </svg>

            {/* 开关 */}
            {selectedSwitches.map((isSelected, index) => (
              <CircularSwitch
                key={`switch-${index}`}
                index={index}
                isSelected={isSelected}
              />
            ))}

            {/* 灯泡 */}
            {lights.map((isOn, index) => (
              <CircularLight key={`light-${index}`} index={index} isOn={isOn} />
            ))}
          </div>

          <div className="text-center space-y-3">
            <button
              onClick={executeOperation}
              disabled={!selectedSwitches.some((s) => s)}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              执行操作
            </button>

            {selectedSwitches.some((s) => s) && (
              <div className="text-xs text-gray-600">
                将按下:{" "}
                {selectedSwitches
                  .map((s, i) => (s ? `S${i + 1}` : ""))
                  .filter((s) => s)
                  .join(", ")}
              </div>
            )}

            {gameMode === "exploration" && lastRotationStep && (
              <div className="text-xs text-purple-600">
                上次旋转了 {lastRotationStep} 格
              </div>
            )}

            <div className="text-xs text-gray-500">
              当前状态：{lights.filter((l) => l).length}个灯已点亮
            </div>
          </div>
        </div>

        {/* 信息面板 */}
        <div>
          {/* 映射关系可视化 */}
          {gameMode === "exploration" && (
            <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
              <h4 className="font-medium mb-3 text-center">当前映射关系</h4>
              <div
                className={`grid gap-2 text-xs ${
                  lightCount <= 4
                    ? "grid-cols-4"
                    : lightCount <= 6
                    ? "grid-cols-3"
                    : "grid-cols-2"
                }`}
              >
                {mapping.map((lightIndex, switchIndex) => (
                  <div
                    key={switchIndex}
                    className="text-center p-2 border rounded"
                  >
                    <div className="text-blue-600 font-medium">
                      S{switchIndex + 1}
                    </div>
                    <div className="text-gray-400">↓</div>
                    <div className="text-yellow-600 font-medium">
                      L{lightIndex + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 状态信息 */}
          <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
            <h4 className="font-medium mb-2">状态信息</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                操作次数: <strong>{moves}</strong>
              </div>
              {/* <div>
                亮灯数:{" "}
                <strong>
                  {lights.filter((l) => l).length}/{lightCount}
                </strong>
              </div> */}
              <div>
                当前模式:{" "}
                <strong>{gameMode === "challenge" ? "挑战" : "探究"}</strong>
              </div>
              {/* <div>
                旋转方式: <strong>随机1-{lightCount}格</strong>
              </div> */}
            </div>
          </div>

          {/* 操作指南 */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-medium mb-2">操作指南：</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• 点击内圈开关(S1-S{lightCount})选择要按下的开关</li>
              <li>• 可以同时选择多个开关</li>
              <li>• 按"执行操作"后开关生效，然后随机旋转</li>
              <li>• 目标：让外圈所有灯泡(L1-L{lightCount})都亮起</li>
              <li>
                •{" "}
                {gameMode === "challenge"
                  ? "挑战模式：看不到“开关-灯”的对应关系"
                  : "探究模式：可以看到“开关-灯”的对应关系"}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 成功提示 */}
      {isComplete && (
        <div className="text-center p-4 bg-green-100 border border-green-300 rounded-lg mb-4">
          <h3 className="text-lg font-bold text-green-800">🎉 太棒了！</h3>
          <p className="text-green-700">你用了 {moves} 步点亮了所有灯！</p>
        </div>
      )}

      {/* 操作历史 */}
      {history.length > 0 && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
          <h4 className="font-medium mb-2">操作历史：</h4>
          <div className="max-h-32 overflow-y-auto text-sm text-gray-600 space-y-1">
            {history.map((move, index) => (
              <div key={index} className="flex justify-between">
                <span>
                  第{index + 1}步: 按下{" "}
                  {move.switches
                    .map((s, i) => (s ? `S${i + 1}` : ""))
                    .filter((s) => s)
                    .join(", ")}
                </span>
                <span>→ 旋转{move.rotationStep}格</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default RotatingSwitchGame;
